import Student from './Student';
import Admin from "./Admin";
import Assignment from "./Assignment";
import ArrayFunctions from './../../Helpers/ArrayFunctions';

export default class ProofreadingOrder {

    private readonly _id: string;
    private readonly _number: string;
    private readonly _deadline: Date;
    private readonly _placedAt: Date;
    private readonly _services: object[];
    private readonly _returningStudent: boolean;
    private readonly _conversionError: boolean;
    private readonly _commentCount: number;
    private readonly _student: Student|null;
    private readonly _assignments: Assignment[];
    private readonly _links: object;
    private readonly _responsibleAdmin: Admin|null;
    private readonly _selectedWords: number;
    private readonly _hasFootnotes: boolean;
    private readonly _proofreadFootnotes: boolean|null;
    private readonly _referenceCount: boolean|null;
    private readonly _document: object|null;
    private readonly _turnaround: number;
    private readonly _status: string;

    constructor({
        id,
        status,
        number,
        deadline,
        placedAt,
        services,
        returningStudent,
        conversionError,
        commentCount,
        student,
        assignments,
        responsibleAdmin,
        links,
        selectedWords,
        hasFootnotes,
        proofreadFootnotes,
        referenceCount,
        document,
        turnaround
    }) {
        this._id = id;
        this._status = status;
        this._number = number;
        this._deadline = new Date(deadline);
        this._placedAt = new Date(placedAt);
        this._services = services;
        this._returningStudent = returningStudent;
        this._conversionError = conversionError;
        this._commentCount = commentCount;
        this._student = student ? new Student(student) : null;
        this._assignments = assignments.map(a => new Assignment(a, this));
        this._links = links;
        this._responsibleAdmin = responsibleAdmin ? new Admin(responsibleAdmin) : null;
        this._selectedWords = selectedWords;
        this._hasFootnotes = hasFootnotes;
        this._proofreadFootnotes = proofreadFootnotes;
        this._referenceCount = referenceCount;
        this._document = document;
        this._turnaround = turnaround;

        this._initViewHelper();
    }

    get id(): string {
        return this._id;
    }

    get number(): string {
        return this._number;
    }

    get deadline(): Date {
        return this._deadline;
    }

    get placedAt(): Date {
        return this._placedAt;
    }

    get services(): object[] {
        return this._services;
    }

    get returningStudent(): boolean {
        return this._returningStudent;
    }

    get conversionError(): boolean {
        return this._conversionError;
    }

    get commentCount(): number {
        return this._commentCount;
    }

    get selectedWords(): number {
        return this._selectedWords;
    }

    get hasFootnotes(): boolean {
        return this._hasFootnotes;
    }

    get proofreadFootnotes(): boolean|null {
        return this._proofreadFootnotes;
    }

    get links(): object {
        return this._links;
    }

    get document(): object | null {
        return this._document;
    }

    get hasLayoutCheck() {
        // @ts-ignore
        return this._services.find(s => s.type === 'layout_check') !== undefined;
    }

    get hasStructureCheck() {
        // @ts-ignore
        return this._services.find(s => s.type === 'structure_check') !== undefined;
    }

    get hasClarityCheck() {
        // @ts-ignore
        return this._services.find(s => s.type === 'clarity_check') !== undefined;
    }

    get hasReferenceCheck() {
        // @ts-ignore
        return this._services.find(s => s.type === 'reference_check') !== undefined;
    }

    get hasProofreadingAndEditing() {
        // @ts-ignore
        return this._services.find(s => s.type === 'proofreading_and_editing') !== undefined;
    }

    get responsibleAdmin() {
        return this._responsibleAdmin;
    }

    get student() {
        return this._student;
    }

    get assignments() {
        return this._assignments;
    }

    get proofreadingAssignmentsNotInterrupted() {
        return this._assignments.filter(a => a.interrupted === false && a.isEditingService);
    }

    get hasParallelProofreadingAssignments(): boolean {
        return this.proofreadingAssignmentsNotInterrupted.length > 1;
    }

    get canBeSplit(): boolean {
        return this.proofreadingAssignmentsNotInterrupted.filter(a => !a.isCompletedOrHigher).length !== 0;
    }

    get referenceCount(): boolean|null
    {
        return this._referenceCount;
    }

    get relevantEditors() {
        return this._assignments.map(a => {
            let invite = a.invites.find(i => i.isWaitingForReply);
            if (a.isNew && invite) {
                return invite.editor;
            }

            if (a.editor) {
                return a.editor;
            }

            return null;
        }).filter((editor, index, self) => {
            if (!editor) {
                return false;
            }
            return self.findIndex(e => e && e.id === editor.id) === index
        });
    }

    public get parallelAssignmentGroups() {
        let groups = [];

        let previousAssignmentIds = this.assignments.map(a => a.previousAssignment ? a.previousAssignment.id : null);
        let [ids, occurrences] = ArrayFunctions.countOccurrences(previousAssignmentIds);

        occurrences.forEach((value, key) => {
            if (value === 1) {
                return;
            }

            // @ts-ignore
            groups.push(this.assignments.filter(a => {
                if (a.previousAssignment === null) {
                    return ids[key] === null;
                }

                return a.previousAssignment.id === ids[key];
            }))
        });

        return groups;
    }

    private _initViewHelper() {
        let currentZindex = 0;
        let lastAssignments = this.assignments.filter(a => a.nextAssignments.length === 0);

        lastAssignments.forEach((assignment, key) => {
            assignment.viewHelper.offset = key;
            this._setPrevious(assignment, currentZindex);
        });
    }


    private _setPrevious(assignment: Assignment, currentZindex) {
        if (!assignment.previousAssignment) {
            return;
        }
        currentZindex ++;
        let group = this._getGroup(assignment);
        let minOffset = Math.min(...group.map(a => a.viewHelper.offset));
        let maxOffset = Math.max(...group.map(a => a.viewHelper.offset));
        // @ts-ignore
        let previousAssignment = this.assignments.find(a => a.id === assignment.previousAssignment.id);
        if (previousAssignment) {
            previousAssignment.viewHelper.offset = (maxOffset + minOffset) / 2;
        }
        this._setPrevious(assignment.previousAssignment, currentZindex);
    }

    private _getGroup(assignment) {
        return this.assignments.filter(a => {
            if (!a.previousAssignment) {
                return !assignment.previousAssignment;
            }

            return assignment.previousAssignment && assignment.previousAssignment.id === a.previousAssignment.id;
        });
    }

    isCancelled(): boolean {
        return this._status === 'cancelled';
    }

    get hasBundleAssignment(): boolean {
        return this._assignments.findIndex(assignment => assignment.isBundle) !== -1;
    }

    get turnaround() {
        return this._turnaround;
    }
}
